import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, Gift, Ticket, Trophy } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { imageSrc } from "../../lib/api";
import { basarStatusMeta, basarUrl, nok, shortDateTime, soldPercent } from "../../lib/basar";
import { useLang } from "../../context/LanguageContext";

/** Oversiktskort for /basar og teaser: namn, arrangør, pris per lodd, premiar, framdrift og sluttdato. Skal kunne skannast på 2 sek. */
export const BasarCard = ({ basar, showOrganizer = true, compact = false }) => {
  const { t } = useLang();
  const st = basarStatusMeta(basar);
  const pct = soldPercent(basar.stats);
  const drawn = basar.status === "drawn";
  return (
    <Link to={basarUrl(basar.id)} className="block group h-full" data-testid="basar-overview-card" data-basar-id={basar.id}>
      <Card className="overflow-hidden card-lift h-full border-border bg-card flex flex-col">
        {!compact && (
          <div className="relative aspect-[16/7] overflow-hidden bg-[hsl(var(--warning))]/10">
            {basar.image_url ? (
              <img src={imageSrc(basar.image_url)} alt="" className="h-full w-full object-cover" loading="lazy" />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-[hsl(var(--warning))]"><Ticket className="w-10 h-10" aria-hidden="true" /></div>
            )}
            <div className="absolute top-3 left-3"><Badge className={`shadow-sm ${st.className}`} data-testid="basar-overview-status">{st.label}</Badge></div>
          </div>
        )}
        <CardContent className={`${compact ? "p-4" : "p-5"} flex flex-col gap-3 flex-1`}>
          <div className="min-w-0">
            <h3 className="font-heading font-semibold leading-snug line-clamp-2" data-testid="basar-overview-name">{basar.name}</h3>
            {showOrganizer && basar.organizer_name && <p className="text-xs text-muted-foreground mt-1 truncate" data-testid="basar-overview-organizer">{basar.organizer_name}</p>}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="outline" className="tabular-nums" data-testid="basar-overview-price">{t("{amount} per lodd", { amount: nok(basar.price) })}</Badge>
            <Badge variant="outline" className="gap-1" data-testid="basar-overview-prizes"><Gift className="w-3 h-3" /> {basar.prizes_count === 1 ? t("{n} premie", { n: 1 }) : t("{n} premier", { n: basar.prizes_count || 0 })}</Badge>
            {compact && <Badge className={st.className} data-testid="basar-overview-status">{st.label}</Badge>}
          </div>
          <div className="space-y-1.5">
            <Progress value={pct} className="h-2 bg-[hsl(var(--warning))]/15 [&>div]:bg-[hsl(var(--warning))]" aria-label={t("Solgte lodd")} data-testid="basar-overview-progress" />
            <p className="text-xs text-muted-foreground tabular-nums" data-testid="basar-overview-sold">{t("Solgt {sold} av {total}", { sold: basar.stats?.sold ?? 0, total: basar.stats?.total ?? 0 })}</p>
          </div>
          <div className="mt-auto flex items-end justify-between gap-3 pt-1">
            <p className="text-xs text-muted-foreground flex items-start gap-1.5 min-w-0">
              {drawn ? <Trophy className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[hsl(var(--warning))]" /> : <CalendarClock className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
              <span className="line-clamp-2">{drawn ? t("Vinnerne er publisert") : basar.draw_info || (basar.sale_end ? t("Salget slutter {date}", { date: shortDateTime(basar.sale_end) }) : t("Trekning kunngjøres av arrangøren"))}</span>
            </p>
            <Button asChild={false} variant="secondary" size="sm" className="pointer-events-none shrink-0" tabIndex={-1} data-testid="basar-overview-open-button">
              {drawn ? t("Se vinnerne") : t("Se basar")} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export const BasarCardGrid = ({ basars, showOrganizer = true, testId = "basar-card-grid" }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid={testId}>
    {basars.map((b) => <BasarCard key={b.id} basar={b} showOrganizer={showOrganizer} />)}
  </div>
);
